/*#
# b3d-quality

The **system quality proxy** — the single place that vends device-appropriate
defaults. The [probe](?b3d-probe.ts) measures the device and feeds a
[`PerfProfile`](?perf-probe.ts) in here; components then resolve any attribute left
at its `auto` sentinel against the current budgets. A global override (`setQuality`)
lets a settings menu force a tier.

This module is deliberately DOM-free (plain state + an observer list, no tosijs
import) so all of its logic is unit-testable headless — the pure-module ethos the
rest of the framework follows.

## The `auto` default convention

Components declare performance-sensitive attributes with an **`auto` sentinel**
(`0` for numbers) instead of a hard-wired number, and resolve them here:

```typescript
// in a component's setup:
const subs = resolveBudget(this.hiResSubdivisions, 'hiResSubdivisions', { xr })
//           explicit author value wins; 0 (auto) → the tier's budget
```

So an author who sets a value gets exactly that; everything left unset adapts to
the measured device. **This is the pattern to reach for wherever you find a
hard-wired default that affects performance** (terrain detail, shadow map size,
reflection resolution, render scaling…): make the default `auto` and resolve it,
rather than baking one number that's wrong for both a phone and a workstation.

## Global override

```javascript
import { setQuality, getQuality } from 'tosijs-3d'

setQuality('low')            // force low everywhere
setQuality('auto')           // back to the measured profile (default)
// a settings <select> calls setQuality(e.target.value) and reads getQuality()
```

## Budgets that are sized once: `headsetDevice()`

A scene is built flat, before any session starts, so a budget that is read
once at build (terrain detail, a pool size, a shadow map) is resolved for the
flat tier and then drawn twice in stereo. On a standalone headset that is the
wrong tier for the whole session. For those, pass `headsetDevice()` as `xr`:

```javascript
import { resolveBudget, headsetDevice } from 'tosijs-3d'

const subs = resolveBudget(this.subdivisions, 'hiResSubdivisions', {
  xr: headsetDevice(),
})
```

It is true on a standalone headset (a Quest), false on a PC driving one. A
budget re-read every frame does not need it: pass the live `xr` state there.
*/
/*{ "parent": "b3d-probe", "order": 20 }*/

import {
  defaultProfile,
  budgetsForTier,
  lowerTier,
  type PerfProfile,
  type PerfBudgets,
  type PerfTier,
} from './perf-probe.js'

/** `'auto'` = use the measured profile; a tier name forces that tier everywhere. */
export type QualitySetting = 'auto' | PerfTier

/** Numeric budget keys — the ones a component resolves from an `auto` (0) sentinel.
 * (`reflections` is a boolean and is read directly, not via `resolveBudget`.) */
export type NumericBudgetKey = Exclude<keyof PerfBudgets, 'reflections'>

let currentProfile: PerfProfile = defaultProfile()
let currentOverride: QualitySetting = 'auto'
const listeners = new Set<(profile: PerfProfile) => void>()

function notify(): void {
  for (const cb of listeners) cb(currentProfile)
}

/** Feed a freshly measured (or cached) profile in — called by the probe. */
export function setPerfProfile(profile: PerfProfile): void {
  currentProfile = profile
  notify()
}

/** The current profile (measured, cached, or the safe default before any probe). */
export function getPerfProfile(): PerfProfile {
  return currentProfile
}

/** Force a global tier, or `'auto'` to follow the measured profile. */
export function setQuality(setting: QualitySetting): void {
  currentOverride = setting
  notify()
}

/** The current override setting. */
export function getQuality(): QualitySetting {
  return currentOverride
}

/**
 * Is this a standalone headset (a Quest, not a PC driving one)?
 *
 * For budgets that are SIZED ONCE AT BUILD. A scene is built flat, before any
 * session, so a build-once budget resolved the ordinary way gets the flat tier
 * and keeps it in stereo: a Quest's terrain was sized for `medium` and drawn
 * twice. Pass this as `xr` for those: `resolveBudget(v, key, { xr:
 * headsetDevice() })`. A forced `quality` still wins, lowered one tier.
 */
export function headsetDevice(): boolean {
  return currentProfile.standaloneHmd === true
}

/** The tier actually in force (override, or the measured tier), for flat or XR. */
export function effectiveTier(opts: { xr?: boolean } = {}): PerfTier {
  const override = getQuality()
  const base = override === 'auto' ? currentProfile.tier : override
  return opts.xr ? lowerTier(base) : base
}

/** The budgets in force for flat or XR rendering. */
export function qualityBudgets(opts: { xr?: boolean } = {}): PerfBudgets {
  const override = getQuality()
  if (override === 'auto') {
    return opts.xr ? currentProfile.xrBudgets : currentProfile.budgets
  }
  const tier = opts.xr ? lowerTier(override) : override
  return budgetsForTier(tier, opts.xr ?? false)
}

/**
 * The engine's hardware-scaling LEVEL for a render — pure, so the rule is
 * tested without a canvas. Babylon renders `canvas / level` pixels, so
 * device resolution is `level = 1 / ratio`; the tier's own `hardwareScaling`
 * (>1 = below native, a fill saving) divides by the ratio on top.
 *
 * - XR: the ratio does not apply (the headset has its own framebuffer).
 * - `explicit > 0` is the author's cap and wins, below 1 included.
 * - Otherwise the display's ratio, capped by the tier (never below 1).
 */
export function renderScalingLevel(
  tierScaling: number,
  xr: boolean,
  explicit: number | null | undefined,
  tierCap: number | undefined,
  devicePixelRatio: number
): number {
  if (xr) return tierScaling
  const dpr = devicePixelRatio > 0 ? devicePixelRatio : 1
  const ratio =
    explicit != null && explicit > 0
      ? explicit
      : Math.max(1, Math.min(dpr, tierCap ?? 1))
  return tierScaling / ratio
}

/**
 * Resolve a component attribute: an explicit positive value wins; the `auto`
 * sentinel (0 / null / undefined / negative) falls back to the current tier's
 * budget for `key`. This is how "if you don't set poolSize / hiResSubdivisions /
 * shadowTextureSize / … it adapts to the device" works.
 */
export function resolveBudget(
  explicit: number | null | undefined,
  key: NumericBudgetKey,
  opts: { xr?: boolean } = {}
): number {
  if (explicit != null && explicit > 0) return explicit
  return qualityBudgets(opts)[key] as number
}

/** Subscribe to quality changes (profile measured, or override set). Returns an
 * unsubscribe. Fires with the current profile; call `qualityBudgets()` to read the
 * effective values (they also depend on the override). */
export function onQualityChange(
  cb: (profile: PerfProfile) => void
): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}
